import { createStateHook } from 'react-sweet-state';

import { messageResearchHasData } from './messageResearchHasData';
import { store } from './store';
import type { ChatMessageResearchStore } from './types';

/**
 * Returns the set of message IDs that have research data (steps or researchPlans).
 * This is a cheap O(n) derivation done once per store update rather than per-message
 * per-render, allowing chat-history to use a Set.has() lookup instead of calling
 * getMessageResearchById for every assistant message on every render.
 */

// Module-level cache so the selector can return a stable Set reference when
// membership is unchanged — react-sweet-state uses referential equality on selector
// output, so returning a new Set on every store update (e.g. a new streamed step for
// an existing message) would trigger a full ChatHistory re-render for every chunk.
let previousIds: Set<string> = new Set();

export const useMessageIdsWithResearchData: () => Set<string> = createStateHook(store, {
	selector: (state: ChatMessageResearchStore): Set<string> => {
		// Iterate Object.keys to avoid the [key, value] tuple allocation from Object.entries.
		const keys = Object.keys(state);
		let changed = keys.length !== previousIds.size;

		const next = new Set<string>();
		for (const messageId of keys) {
			if (messageResearchHasData(state[messageId])) {
				next.add(messageId);
				if (!previousIds.has(messageId)) {
					changed = true;
				}
			}
		}

		// Also detect removals: a message ID was in previousIds but has no research data now.
		// This can happen if steps are emptied without deleting the store entry — in that case
		// keys.length === previousIds.size so the initial check doesn't catch it, and the loop
		// only detects additions (new IDs in next not in previousIds), not removals.
		if (!changed && previousIds.size !== next.size) {
			changed = true;
		}

		if (!changed) {
			return previousIds;
		}

		previousIds = next;
		return next;
	},
});
